import { useEffect, useState } from "react";
import { Share, MoreVertical, PlusSquare, Smartphone, Download, ChevronDown, ChevronUp } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function useMitarbeiterManifest() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const prev = link.getAttribute("href");
    link.setAttribute("href", "/mitarbeiter-manifest.json");
    return () => link.setAttribute("href", prev);
  }, []);
}

const Schritt = ({ n, icon: Icon, children }) => <li className="flex items-start gap-3"><span className="h-6 w-6 rounded-full bg-cyan-700 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</span><span className="text-sm text-slate-700 leading-relaxed">{Icon && <Icon className="inline h-4 w-4 -mt-0.5 mx-0.5 text-slate-500" />}{children}</span></li>;

export default function HomescreenAnleitung() {
  useMitarbeiterManifest();
  const [tab, setTab] = useState(isIOS() ? "ios" : "android");
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (isStandalone()) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="homescreen-anleitung">
      <button type="button" onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 text-left" data-testid="homescreen-toggle">
        <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0"><Smartphone className="h-5 w-5" /></div>
        <div className="flex-1"><p className="font-semibold text-slate-900 text-sm">Mitarbeiterbereich auf den Homescreen holen</p><p className="text-xs text-slate-500">Wie eine App öffnen – ohne Browser-Adresszeile</p></div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {prompt && <button type="button" onClick={async () => { prompt.prompt(); await prompt.userChoice; setPrompt(null); }} className="w-full h-12 rounded-xl bg-slate-900 text-white font-semibold inline-flex items-center justify-center gap-2" data-testid="homescreen-install-btn"><Download className="h-4 w-4" />Jetzt als App installieren</button>}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {[["ios", "iPhone / iPad"], ["android", "Android"]].map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} className={`flex-1 h-9 rounded-lg text-sm font-semibold ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid={`homescreen-tab-${k}`}>{l}</button>)}
          </div>
          {tab === "ios" ? (
            <ol className="space-y-2.5" data-testid="homescreen-ios">
              <Schritt n={1}>Diese Seite in <strong>Safari</strong> öffnen (nicht in Chrome oder einer anderen App).</Schritt>
              <Schritt n={2} icon={Share}>Unten in der Leiste auf das <strong>Teilen-Symbol</strong> tippen (Quadrat mit Pfeil nach oben).</Schritt>
              <Schritt n={3} icon={PlusSquare}>Im Menü nach unten wischen und <strong>„Zum Home-Bildschirm“</strong> wählen.</Schritt>
              <Schritt n={4}>Oben rechts auf <strong>„Hinzufügen“</strong> tippen. Das Symbol „L&amp;W Mitarbeiter“ erscheint auf dem Homescreen.</Schritt>
            </ol>
          ) : (
            <ol className="space-y-2.5" data-testid="homescreen-android">
              <Schritt n={1}>Diese Seite in <strong>Chrome</strong> öffnen.</Schritt>
              <Schritt n={2} icon={MoreVertical}>Oben rechts auf die <strong>drei Punkte</strong> tippen.</Schritt>
              <Schritt n={3} icon={PlusSquare}><strong>„Zum Startbildschirm hinzufügen“</strong> bzw. <strong>„App installieren“</strong> wählen.</Schritt>
              <Schritt n={4}>Mit <strong>„Hinzufügen“</strong> bestätigen. Bei Samsung Internet: Menü (drei Striche) → „Seite hinzufügen zu“ → „Startbildschirm“.</Schritt>
            </ol>
          )}
          <p className="text-xs text-slate-500">Tipp: Nach dem Login bleiben Sie auf diesem Gerät angemeldet, bis Sie sich abmelden oder die Verwaltung den Zugang sperrt.</p>
        </div>
      )}
    </div>
  );
}
